import { useQuery, useMutation, useQueryClient } from 'react-query';
import { axiosClient } from '@/services/utilities';
import { useSearchParams } from 'react-router-dom';

export interface PublisherJourneyItem {
  id: number;
  username: string;
  foundUsBy: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  executedBy: string | null;
  executedAt?: string;
  executionReason?: string;
}

export interface PublisherJourneyResponse {
  success: boolean;
  data: PublisherJourneyItem[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface UpdateStatusRequest {
  status: 'APPROVED' | 'REJECTED';
  executedBy: string;
  userEmail?: string;
  fullname?: string;
  username?: string;
  executionReason?: string;
}

export interface UpdateStatusResponse {
  success: boolean;
  message: string;
}

export interface UsePublisherJourneyQueryParams {
  offset?: number;
  limit?: number;
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: string;
  status?: string;
  username?: string;
}

export const usePublisherJourneyQuery = (params?: UsePublisherJourneyQueryParams) => {
  const [searchParam] = useSearchParams();
  
  // Get parameters from URL or use defaults
  const page = Number(searchParam.get('page')) || 1;
  const size = Number(searchParam.get('size')) || 10;
  const findUsers = searchParam.get('findUsers') || '';
  const status = searchParam.get('status') || '';

  // Calculate offset from page
  const offset = (page - 1) * size;

  // Merge URL params with passed params
  const queryParams: UsePublisherJourneyQueryParams = {
    offset: params?.offset ?? offset,
    limit: params?.limit ?? size,
    sortOrder: params?.sortOrder ?? 'DESC',
    sortBy: params?.sortBy ?? 'createdAt',
    status: params?.status ?? status,
    username: params?.username ?? findUsers,
    ...params,
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PublisherJourneyItem[] | PublisherJourneyResponse>({
    queryKey: ['publisher-journey', queryParams],
    queryFn: async () => {
      // Build query string from parameters
      const searchParams = new URLSearchParams();
      
      // Map camelCase to snake_case for API
      const apiParams = {
        offset: queryParams.offset,
        limit: queryParams.limit,
        sort_order: queryParams.sortOrder,
        sort_by: queryParams.sortBy,
        status: queryParams.status,
        username: queryParams.username,
      };
      
      Object.entries(apiParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      const response = await axiosClient.get(
        `/req-as-publisher/publisher-journey?${searchParams.toString()}`
      );

      // Return the full response properties
      return {
        data: response.data,
        total: (response as any).total,
        success: (response as any).success
      };
    },
    enabled: true, // Always enabled, can be controlled from component
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes stale time - reduce unnecessary refetches
    cacheTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: false, // Disable refetch when window gains focus
    refetchOnMount: true, // Enable refetch on mount to ensure fresh data
  });

  // Handle loading state - return empty data when loading
  if (isLoading || !data) {
    return {
      data: [],
      total: 0,
      page: page,
      limit: size,
      isLoading,
      isFetching,
      isError,
      error,
      refetch,
    };
  }

  // Type guard to check if data is array or response object
  const isArrayResponse = Array.isArray(data);
  
  // Based on the response structure, we need to handle different cases
  let actualData: PublisherJourneyItem[] = [];
  let totalCount = 0;

  if (isArrayResponse) {
    // Case 1: data is array directly
    actualData = data as PublisherJourneyItem[];
    totalCount = actualData.length;
  } else {
    // Case 2: data is object with properties
    const responseObj = data as any;
    
    // Check if data has 'data' property (nested structure from our service function)
    if (responseObj.data && Array.isArray(responseObj.data.data)) {
      actualData = responseObj.data.data;
      totalCount = responseObj.data.total || responseObj.data.data.length;
    } else if (responseObj.data && Array.isArray(responseObj.data)) {
      actualData = responseObj.data;
      totalCount = responseObj.total || responseObj.data.length;
    } else {
      // Direct object properties
      actualData = responseObj;
      totalCount = responseObj.total || 0;
    }
    
  }

  return {
    data: actualData,
    total: totalCount,
    page: page,
    limit: size,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

// Hook for getting a single publisher journey by ID
export const usePublisherJourneyById = (id: string | number) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PublisherJourneyItem>({
    queryKey: ['publisher-journey', id],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/req-as-publisher/publisher-journey/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

// Hook for updating publisher journey status (approve/reject)
export const useUpdatePublisherJourneyStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UpdateStatusResponse, Error, { id: string | number; data: UpdateStatusRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await axiosClient.patch(
        `/req-as-publisher/publisher-journey/${id}/status`,
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch publisher journey list
      queryClient.invalidateQueries(['publisher-journey']);
      // Invalidate and refetch single publisher journey
      queryClient.invalidateQueries(['publisher-journey', variables.id]);
      // Also refetch to ensure the latest data is displayed
      queryClient.refetchQueries(['publisher-journey']);
    },
  });
};